import { Component, OnInit, ViewChild } from '@angular/core';
import { AuctionSummaryService, PurchasedItemDto, ReportType, SellerRemitDto, SellToggleState, SettlementBatchService } from 'remarketing-angular-library';
import { Store } from '@ngxs/store';
import { Router } from '@angular/router';
import { AnalyticsService, SaComponent } from 'smartauction-frontend-micro-library';
import { ReportUtilityService } from '../../../../../app/modules/reports/services/report-utility.service';
import { get } from 'lodash';
import { take, takeUntil } from 'rxjs/operators';
import { ErrorModalComponent } from '@ally/remarketing/shared/ui';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { LaunchDarklyService } from 'remarketing-angular-library';
import { ReportHeaderType, ReportRole } from '../../../../../app/modules/reports/enums';
import {
  ReportPreviewComponent
} from '../../../../../app/modules/reports/components/report-preview/report-preview.component';
import { SpinnerComponent } from 'remarketing-ui-components-lib';


@Component({
    selector: 'app-sell-report-preview-shell',
    templateUrl: './sell-report-preview-shell.component.html',
    styleUrls: ['./sell-report-preview-shell.component.scss'],
    imports: [TranslocoDirective, ReportPreviewComponent, ErrorModalComponent, SpinnerComponent]
})
export class SellReportPreviewShellComponent extends SaComponent implements OnInit {
  @ViewChild(ErrorModalComponent) errorModal: ErrorModalComponent;

  private _activePreviewType: ReportType;
  private _purchasedItemDtos: PurchasedItemDto[] = [];
  reportHeaderType = ReportHeaderType;
  reportRole = ReportRole.SELLER;
  private _sellerRemitDtos: SellerRemitDto[] = [];
  isLoading = true;
  multipleBuyersSelected = false;
  scrollPosition;
  errorMessage: any;

  constructor(
    private readonly store: Store,
    private readonly route: Router,
    private readonly _analyticsService: AnalyticsService,
    private readonly _auctionSummaryService: AuctionSummaryService,
    private readonly _settlementBatchService: SettlementBatchService,
    private readonly _reportUtilityService: ReportUtilityService,
    private readonly translocoService: TranslocoService,
    private readonly launchDarklyService: LaunchDarklyService
  ) {
    super();
    this.scrollPosition = get(this.route.currentNavigation(), 'extras.state.scrollPosition', 0);
  }

  ngOnInit() {
    this._activePreviewType = ReportType.AUTHORIZATION;
    this.populatePurchasedItemListOrRoute();
    this.trackPageLoad(this._activePreviewType);
    this.getTranslations();
  }

  getTranslations() {
    this.translocoService
      .selectTranslateObject('sellModule.manageInventory.sellReportPreviewShell.emailReportErrorForDifferentBuyers')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(value => {
        this.errorMessage = value;
      });
  }

  ngOnDestroy() {
    // revoke URL of the previously generated report
    super.ngOnDestroy();
    this._reportUtilityService.revokeUrl();
  }

  trackPageLoad(view: ReportType) {
    const title = this.getTitle(view);
    this._analyticsService.trackPageLoad({
      page: {
        pageName: `${this._analyticsService.digitalData.page.category.site}:Sell:Manage Inventory:${
          this.headerType === this.reportHeaderType.TABULAR ? `Auth Report${title}` : 'View Reports'
        }`,
        section: 'Sell',
        site: this._analyticsService.digitalData.page.category.site,
        title,
        vehicleList: this.getInventoryItemUuids()
      }
    });
  }

  getTitle(view: ReportType) {
    switch (view) {
      case ReportType.RECORD_OF_SALE:
        return ':Record of Sale';
      case ReportType.BOTH:
        return ':Both';
      default:
        return '';
    }
  }

  set activePreviewType(activePreview: ReportType) {
    this._activePreviewType = activePreview;
    this.trackPageLoad(this._activePreviewType);
  }

  get activePreviewType() {
    return this._activePreviewType;
  }

  get purchasedItemDtos(): PurchasedItemDto[] {
    return this._purchasedItemDtos;
  }

  get sellerRemitDtos(): SellerRemitDto[] {
    return this._sellerRemitDtos;
  }

  get headerType(): ReportHeaderType {
    return this.launchDarklyService.getFeatureFlag('auctionSummary-reportsDropdownEnabled', false)
      ? ReportHeaderType.DROPDOWN
      : ReportHeaderType.TABULAR;
  }

  isTenantCanadian() {
    return this.launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  populatePurchasedItemListOrRoute() {
    const selectedAuctionItemUuids = this.store
      .selectSnapshot(SellToggleState.selectedVehicles)
      .map(auctionItem => auctionItem.auctionItemUuid);
    this._auctionSummaryService
      .getReportPreviewData(selectedAuctionItemUuids)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(response => {
        this._purchasedItemDtos = response.purchasedItems;
        this.checkMultipleBuyers();
        if (this._purchasedItemDtos.length === 0) {
          this.route.navigate(['/inventory']);
        }
        this.isLoading = false;
        this.populateSettlementRemitData();
      });
  }

  populateSettlementRemitData() {
    if (this.isTenantCanadian) {
      this._settlementBatchService
        .getSellerRemitDtoForAuctionItems({
          auctionItemUuids: this.purchasedItemDtos.map(purchasedItemDto => purchasedItemDto.auctionItemUuid)
        })
        .pipe(take(1))
        .subscribe(
          dto => {
            this._sellerRemitDtos = dto;
          },
          error => {
            console.log(error);
          }
        );
    }
  }

  printPurchaseItems() {
    this._reportUtilityService.printPurchasedItems(this._purchasedItemDtos, this._activePreviewType);
  }

  showErrorModal() {
    this.errorModal.message = this.errorMessage;
    this.errorModal.show();
  }

  checkMultipleBuyers() {
    const selectedAuctionItemBuyerOrgEntities = this._purchasedItemDtos.map(
      purchasedItemDto => purchasedItemDto.buyer.oeUuid
    );
    const buyerUuidSet = new Set(selectedAuctionItemBuyerOrgEntities);
    this.multipleBuyersSelected = buyerUuidSet.size > 1;
  }

  getInventoryItemUuids(): string[] {
    const inventoryItemUuids = [];
    for (const purchasedItem of this._purchasedItemDtos) {
      inventoryItemUuids.push(purchasedItem.inventoryItemUuid);
    }
    return inventoryItemUuids;
  }
}
